if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // ISELA E5B.1: Farbgruppen-Synchronisation.
        // Dawn gleicht beim Variantenwechsel nur die Viewer-Liste ab — die Thumbnail-Liste
        // bliebe auf der alten Farbe stehen. Wir haengen uns an das bereits vorhandene
        // variantChange-Event an: keine zusaetzliche Bibliothek, kein MutationObserver,
        // kein Polling. Der Handler ist ein No-op, solange die Gruppierung nicht greift.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.iselaUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onIselaVariantChange.bind(this));
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnails();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (this.iselaUnsubscribe) this.iselaUnsubscribe();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            // ISELA E5B.1: Bei aktiver Farbfilterung kann es zu einer Media-ID kein
            // Thumbnail geben. Ohne diese Pruefung wirft der Variantenwechsel hier.
            activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // ISELA E5B.1: Zwischen Aufruf und Timeout kann das Element durch den
          // Galerie-Abgleich aus dem DOM geflogen sein. Ohne diese Pruefung wirft der
          // Farbwechsel hier eine TypeError-Ausnahme.
          if (!activeMedia.parentElement) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      // ISELA E5B.1: Klick-Handler der Thumbnails binden. Ausgelagert, weil die Liste nach
      // einem Farbwechsel serverseitig neu gerendert wird und die Handler dann fehlen.
      bindThumbnails() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      // ISELA E5B.1: Nach dem Variantenwechsel Galerie und Thumbnails auf die neue
      // Farbgruppe bringen. Greift nur, wenn die Gruppierung vorher oder nachher aktiv
      // ist — sonst bleibt das Dawn-Verhalten unangetastet.
      onIselaVariantChange(event) {
        const html = event?.data?.html;
        if (!html) return;
        const incoming = html.querySelector('media-gallery');
        if (!incoming) return;

        const wasGrouped = this.hasAttribute('data-isela-media-grouping');
        const isGrouped = incoming.hasAttribute('data-isela-media-grouping');

        // T-009.1: Auch ohne Farbgruppierung eingreifen, wenn die alte oder neue
        // Variante kein gebundenes Medium hat. Dawn bricht updateMedia() dann ab und
        // liesse das Bild der vorigen Farbe stehen — gemessen live beim Farbwechsel.
        // Der Server liefert fuer diesen Fall die richtige Liste bzw. "Foto folgt".
        const wasOhneBild = this.hasAttribute('data-isela-variant-ohne-bild');
        const isOhneBild = incoming.hasAttribute('data-isela-variant-ohne-bild');
        if (!wasGrouped && !isGrouped && !wasOhneBild && !isOhneBild) return;
        this.toggleAttribute('data-isela-variant-ohne-bild', isOhneBild);

        if (isGrouped) {
          this.setAttribute('data-isela-media-grouping', incoming.getAttribute('data-isela-media-grouping'));
        } else {
          this.removeAttribute('data-isela-media-grouping');
        }

        // Viewer: Dawn gleicht in updateMedia() selbst ab, sobald die Variante ein
        // gebundenes Medium hat. Wichtig — dabei VERSCHIEBT Dawn Knoten aus dem geparsten
        // Dokument ins DOM. Die eingehende Liste ist danach unvollstaendig und darf nicht
        // mehr als Vergleichsgrundlage dienen. Wir springen deshalb nur ein, wenn Dawn
        // gar nicht abgeglichen hat: bei Varianten ohne gebundenes Medium.
        const dawnHatViewerAbgeglichen = Boolean(event?.data?.variant?.featured_media?.id);
        if (!dawnHatViewerAbgeglichen) {
          const viewerList = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
          const incomingViewerList = incoming.querySelector('[id^="Slider-Gallery"]');
          if (viewerList && incomingViewerList) {
            viewerList.innerHTML = incomingViewerList.innerHTML;
            this.elements.viewer.resetPages?.();
          }

          // T-009.1: Dawn tauscht auch den Lightbox-Inhalt nur in updateMedia(). Ohne
          // gebundenes Medium blieben dort die Bilder der vorigen Farbe liegen.
          const sectionId = this.id.replace('MediaGallery-', '');
          const modalContent = document.querySelector('#ProductModal-' + sectionId + ' .product-media-modal__content');
          const incomingModalContent = html.querySelector('product-modal .product-media-modal__content');
          if (modalContent && incomingModalContent) modalContent.innerHTML = incomingModalContent.innerHTML;
        }

        const thumbList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        const incomingThumbList = incoming.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbList && incomingThumbList && thumbList.innerHTML !== incomingThumbList.innerHTML) {
          thumbList.innerHTML = incomingThumbList.innerHTML;
          this.bindThumbnails();
          this.elements.thumbnails.resetPages?.();
        }

        // Bildzaehler serverseitig uebernehmen. slider-component rechnet den Wert bei
        // weniger als zwei Slides nicht neu.
        const total = this.querySelector('.slider-counter--total');
        const incomingTotal = incoming.querySelector('.slider-counter--total');
        if (total && incomingTotal) total.textContent = incomingTotal.textContent;

        const active =
          this.elements.viewer?.querySelector('[data-media-id].is-active') ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (active) this.setActiveMedia(active.dataset.mediaId, false);
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
